// src/components/TaskTable.js
import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchTasks, updateTaskStatus } from '../features/tasks/taskSlice';
import { Link } from 'react-router-dom';
const TaskTable = ({ tasks }) => {
    const [sortBy, setSortBy] = useState('priority_score');
    const [sortDirection, setSortDirection] = useState('desc');

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortDirection('desc');
        }
    };
    const dispatch = useDispatch();

    const handleMarkCompleted = async (taskId) => {
        if (window.confirm('Mark this task as completed?')) {
            try {
                await dispatch(updateTaskStatus({
                    taskId,
                    status: 'completed'
                })).unwrap();
                alert('Task marked as completed!');
            } catch (error) {
                alert('Failed to update task: ' + error.message);
            }
        }
    };
    const sortedTasks = [...tasks].sort((a, b) => {
        if (sortDirection === 'asc') {
            return a[sortBy] > b[sortBy] ? 1 : -1;
        } else {
            return a[sortBy] < b[sortBy] ? 1 : -1;
        }
    });

    const getPriorityClass = (score) => {
        if (score >= 8) return 'priority-critical';
        if (score >= 6) return 'priority-high';
        if (score >= 4) return 'priority-medium';
        return 'priority-low';
    };

    const getComplexityIcon = (complexity) => {
        if (complexity <= 3) return '🟢';
        if (complexity <= 6) return '🟡';
        if (complexity <= 8) return '🟠';
        return '🔴';
    };

    const getUrgencyIcon = (urgency) => {
        if (urgency >= 8) return '🚨';
        if (urgency >= 6) return '⚡';
        if (urgency >= 4) return '⏰';
        return '📅';
    };

    const getImportanceIcon = (importance) => {
        if (importance >= 8) return '⭐';
        if (importance >= 6) return '🌟';
        if (importance >= 4) return '✨';
        return '💫';
    };

    return (
        <div className="task-table">
            <div className="table-header">
                <h3>📊 Task Priority Analysis</h3>
                <p>Click column headers to sort • {sortedTasks.length} tasks analyzed</p>
            </div>

            <div className="table-container">
                <table>
                    <thead>
                        <tr>
                            <th
                                className={`sortable ${sortBy === 'title' ? 'sorted' : ''}`}
                                onClick={() => handleSort('title')}
                            >
                                <div className="th-content">
                                    <span>📝 Task Title</span>
                                    <span className="sort-icon">
                                        {sortBy === 'title' && (sortDirection === 'asc' ? '▲' : '▼')}
                                    </span>
                                </div>
                            </th>
                            <th
                                className={`sortable ${sortBy === 'importance' ? 'sorted' : ''}`}
                                onClick={() => handleSort('importance')}
                            >
                                <div className="th-content">
                                    <span>⭐ Importance</span>
                                    <span className="sort-icon">
                                        {sortBy === 'importance' && (sortDirection === 'asc' ? '▲' : '▼')}
                                    </span>
                                </div>
                            </th>
                            <th
                                className={`sortable ${sortBy === 'urgency' ? 'sorted' : ''}`}
                                onClick={() => handleSort('urgency')}
                            >
                                <div className="th-content">
                                    <span>⚡ Urgency</span>
                                    <span className="sort-icon">
                                        {sortBy === 'urgency' && (sortDirection === 'asc' ? '▲' : '▼')}
                                    </span>
                                </div>
                            </th>
                            <th
                                className={`sortable ${sortBy === 'complexity' ? 'sorted' : ''}`}
                                onClick={() => handleSort('complexity')}
                            >
                                <div className="th-content">
                                    <span>🎯 Complexity</span>
                                    <span className="sort-icon">
                                        {sortBy === 'complexity' && (sortDirection === 'asc' ? '▲' : '▼')}
                                    </span>
                                </div>
                            </th>
                            <th
                                className={`sortable ${sortBy === 'estimated_hours' ? 'sorted' : ''}`}
                                onClick={() => handleSort('estimated_hours')}
                            >
                                <div className="th-content">
                                    <span>⏱️ Est. Hours</span>
                                    <span className="sort-icon">
                                        {sortBy === 'estimated_hours' && (sortDirection === 'asc' ? '▲' : '▼')}
                                    </span>
                                </div>
                            </th>
                            <th
                                className={`sortable priority-header ${sortBy === 'priority_score' ? 'sorted' : ''}`}
                                onClick={() => handleSort('priority_score')}
                            >
                                <div className="th-content">
                                    <span>🏆 Priority Score</span>
                                    <span className="sort-icon">
                                        {sortBy === 'priority_score' && (sortDirection === 'asc' ? '▲' : '▼')}
                                    </span>
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedTasks.map((task, index) => (
                            <tr key={task.id} className={`task-row ${getPriorityClass(task.priority_score)}`}>
                                <td className="task-title">
                                    <div className="title-content">
                                        <span className="rank-badge">#{index + 1}</span>
                                        <div className="title-text">
                                            <strong>{task.title}</strong>
                                            {task.description && (
                                                <div className="task-description">{task.description}</div>
                                            )}
                                        </div>
                                    </div>
                                </td>
                                <td className="metric-cell">
                                    <div className="metric-content">
                                        <span className="metric-icon">{getImportanceIcon(task.importance)}</span>
                                        <span className="metric-value">{task.importance}/10</span>
                                        <div className="metric-bar">
                                            <div
                                                className="metric-fill importance-fill"
                                                style={{ width: `${task.importance * 10}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </td>
                                <td className="metric-cell">
                                    <div className="metric-content">
                                        <span className="metric-icon">{getUrgencyIcon(task.urgency)}</span>
                                        <span className="metric-value">{task.urgency}/10</span>
                                        <div className="metric-bar">
                                            <div
                                                className="metric-fill urgency-fill"
                                                style={{ width: `${task.urgency * 10}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </td>
                                <td className="metric-cell">
                                    <div className="metric-content">
                                        <span className="metric-icon">{getComplexityIcon(task.complexity)}</span>
                                        <span className="metric-value">{task.complexity}/10</span>
                                        <div className="metric-bar">
                                            <div
                                                className="metric-fill complexity-fill"
                                                style={{ width: `${task.complexity * 10}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </td>
                                <td className="hours-cell">
                                    <div className="hours-content">
                                        <span className="hours-value">{task.estimated_hours}h</span>
                                        <div className="hours-indicator">
                                            {task.estimated_hours <= 2 ? '🟢' :
                                                task.estimated_hours <= 8 ? '🟡' :
                                                    task.estimated_hours <= 16 ? '🟠' : '🔴'}
                                        </div>
                                    </div>
                                </td>
                                <td className="priority-cell">
                                    <div className={`priority-score ${getPriorityClass(task.priority_score)}`}>
                                        <span className="score-value">{task.priority_score.toFixed(1)}</span>
                                        <div className="priority-badge">
                                            {task.priority_score >= 8 ? 'CRITICAL' :
                                                task.priority_score >= 6 ? 'HIGH' :
                                                    task.priority_score >= 4 ? 'MEDIUM' : 'LOW'}
                                        </div>
                                    </div>
                                </td>
                                <td className="task-actions">
                                    <Link to={`/tasks/${task.id}`} className="btn-text">
                                        View Details
                                    </Link>
                                    {task.status !== 'completed' && (
                                        <button
                                            className="btn-text success"
                                            onClick={() => handleMarkCompleted(task.id)}
                                        >
                                            Mark Completed
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <style jsx>{`
            /* TaskTable Styles */
            .task-table {
                width: 100%;
                background: white;
                border-radius: 16px;
                overflow: hidden;
                box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
                border: 1px solid rgba(255, 255, 255, 0.2);
            }

            .table-header {
                padding: 25px 30px 20px;
                background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
                border-bottom: 2px solid #dee2e6;
                position: relative;
                overflow: hidden;
            }

            .table-header::before {
                content: '';
                position: absolute;
                top: 0;
                left: -100%;
                width: 100%;
                height: 100%;
                background: linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.05), transparent);
                animation: shimmer 3s ease-in-out infinite;
            }

            @keyframes shimmer {
                0%, 100% { left: -100%; }
                50% { left: 100%; }
            }

            .table-header h3 {
                margin: 0 0 8px 0;
                font-size: 22px;
                font-weight: 700;
                color: #495057;
                position: relative;
                z-index: 2;
            }

            .table-header p {
                margin: 0;
                font-size: 14px;
                color: #6c757d;
                font-weight: 500;
                position: relative;
                z-index: 2;
            }

            /* Table Container */
            .table-container {
                overflow-x: auto;
                max-height: 600px;
                overflow-y: auto;
                position: relative;
            }

            .table-container::-webkit-scrollbar {
                width: 12px;
                height: 12px;
            }

            .table-container::-webkit-scrollbar-track {
                background: #f1f3f4;
                border-radius: 6px;
            }

            .table-container::-webkit-scrollbar-thumb {
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                border-radius: 6px;
                border: 2px solid #f1f3f4;
            }

            .table-container::-webkit-scrollbar-thumb:hover {
                background: linear-gradient(135deg, #5a6fd8 0%, #6c4f9c 100%);
            }

            /* Table Styles */
            table {
                width: 100%;
                border-collapse: separate;
                border-spacing: 0;
                font-size: 14px;
                min-width: 800px;
            }

            /* Table Header */
            thead {
                position: sticky;
                top: 0;
                z-index: 10;
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            }

            thead th {
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                color: white;
                font-weight: 600;
                font-size: 13px;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                padding: 0;
                border: none;
                position: relative;
                box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
            }

            .th-content {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 18px 16px;
                cursor: pointer;
                transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                position: relative;
                overflow: hidden;
                min-height: 24px;
            }

            .th-content::before {
                content: '';
                position: absolute;
                top: 0;
                left: -100%;
                width: 100%;
                height: 100%;
                background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
                transition: left 0.6s;
            }

            .sortable:hover .th-content::before {
                left: 100%;
            }

            .sortable:hover .th-content {
                background: rgba(255, 255, 255, 0.1);
                transform: translateY(-1px);
            }

            .sortable.sorted .th-content {
                background: rgba(255, 255, 255, 0.15);
                box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
            }

            .sort-icon {
                font-size: 12px;
                opacity: 0.8;
                transition: all 0.3s ease;
                margin-left: 8px;
                min-width: 12px;
                text-align: center;
            }

            .priority-header .th-content {
                background: linear-gradient(135deg, #ffd700 0%, #ffb347 100%);
                color: #333;
                font-weight: 700;
            }

            /* Table Body */
            tbody tr {
                transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                border-bottom: 1px solid #f1f3f4;
                position: relative;
            }

            tbody tr::before {
                content: '';
                position: absolute;
                left: 0;
                top: 0;
                width: 0;
                height: 100%;
                background: linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.1), transparent);
                transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
                z-index: 1;
            }

            tbody tr:hover::before {
                width: 100%;
            }

            tbody tr:hover {
                background: rgba(102, 126, 234, 0.05);
                transform: translateY(-2px);
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            }

            tbody tr:nth-child(even) {
                background: rgba(248, 249, 250, 0.5);
            }

            tbody tr:nth-child(even):hover {
                background: rgba(102, 126, 234, 0.05);
            }

            /* Priority Row Classes */
            .task-row.priority-critical {
                border-left: 4px solid #dc3545;
                background: linear-gradient(90deg, rgba(220, 53, 69, 0.05) 0%, transparent 100%);
            }

            .task-row.priority-high {
                border-left: 4px solid #fd7e14;
                background: linear-gradient(90deg, rgba(253, 126, 20, 0.05) 0%, transparent 100%);
            }

            .task-row.priority-medium {
                border-left: 4px solid #ffc107;
                background: linear-gradient(90deg, rgba(255, 193, 7, 0.05) 0%, transparent 100%);
            }

            .task-row.priority-low {
                border-left: 4px solid #28a745;
                background: linear-gradient(90deg, rgba(40, 167, 69, 0.05) 0%, transparent 100%);
            }

            /* Table Cells */
            td {
                padding: 20px 16px;
                vertical-align: middle;
                position: relative;
                z-index: 2;
                background: transparent;
            }

            /* Task Title Cell */
            .task-title {
                min-width: 250px;
                max-width: 350px;
            }

            .title-content {
                display: flex;
                align-items: flex-start;
                gap: 12px;
            }

            .rank-badge {
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                color: white;
                font-size: 11px;
                font-weight: 700;
                padding: 4px 8px;
                border-radius: 12px;
                min-width: 28px;
                text-align: center;
                flex-shrink: 0;
                box-shadow: 0 2px 6px rgba(102, 126, 234, 0.3);
            }

            .title-text {
                flex: 1;
                min-width: 0;
            }

            .title-text strong {
                display: block;
                font-size: 15px;
                font-weight: 600;
                color: #495057;
                line-height: 1.4;
                margin-bottom: 4px;
                word-wrap: break-word;
            }

            .task-description {
                font-size: 12px;
                color: #6c757d;
                line-height: 1.4;
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                overflow: hidden;
                margin-top: 4px;
            }

            /* Metric Cells */
            .metric-cell {
                min-width: 120px;
                width: 120px;
            }

            .metric-content {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 6px;
            }

            .metric-icon {
                font-size: 18px;
                margin-bottom: 2px;
            }

            .metric-value {
                font-size: 13px;
                font-weight: 600;
                color: #495057;
                min-width: 35px;
                text-align: center;
            }

            .metric-bar {
                width: 80px;
                height: 6px;
                background: #e9ecef;
                border-radius: 3px;
                overflow: hidden;
                position: relative;
            }

            .metric-fill {
                height: 100%;
                border-radius: 3px;
                transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
                position: relative;
                overflow: hidden;
            }

            .metric-fill::after {
                content: '';
                position: absolute;
                top: 0;
                left: -100%;
                width: 100%;
                height: 100%;
                background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
                animation: fillShimmer 2s ease-in-out infinite;
            }

            @keyframes fillShimmer {
                0%, 100% { left: -100%; }
                50% { left: 100%; }
            }

            .importance-fill {
                background: linear-gradient(135deg, #ffd700 0%, #ffb347 100%);
            }

            .urgency-fill {
                background: linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%);
            }

            .complexity-fill {
                background: linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%);
            }

            /* Hours Cell */
            .hours-cell {
                min-width: 100px;
                width: 100px;
            }

            .hours-content {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 6px;
            }

            .hours-value {
                font-size: 15px;
                font-weight: 600;
                color: #495057;
            }

            .hours-indicator {
                font-size: 16px;
            }

            /* Priority Cell */
            .priority-cell {
                min-width: 140px;
                width: 140px;
            }

            .priority-score {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 8px;
                padding: 12px;
                border-radius: 12px;
                position: relative;
                overflow: hidden;
            }

            .priority-score::before {
                content: '';
                position: absolute;
                top: 0;
                left: -100%;
                width: 100%;
                height: 100%;
                background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
                transition: left 0.6s;
            }

            .priority-score:hover::before {
                left: 100%;
            }

            .score-value {
                font-size: 24px;
                font-weight: 800;
                position: relative;
                z-index: 2;
                text-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
            }

            .priority-badge {
                font-size: 10px;
                font-weight: 700;
                padding: 4px 8px;
                border-radius: 8px;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                position: relative;
                z-index: 2;
                box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
            }

            /* Priority Score Colors */
            .priority-score.priority-critical {
                background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
                color: white;
            }

            .priority-score.priority-critical .priority-badge {
                background: rgba(255, 255, 255, 0.2);
                color: white;
            }

            .priority-score.priority-high {
                background: linear-gradient(135deg, #fd7e14 0%, #e55a00 100%);
                color: white;
            }

            .priority-score.priority-high .priority-badge {
                background: rgba(255, 255, 255, 0.2);
                color: white;
            }

            .priority-score.priority-medium {
                background: linear-gradient(135deg, #ffc107 0%, #e0a800 100%);
                color: #333;
            }

            .priority-score.priority-medium .priority-badge {
                background: rgba(0, 0, 0, 0.1);
                color: #333;
            }

            .priority-score.priority-low {
                background: linear-gradient(135deg, #28a745 0%, #1e7e34 100%);
                color: white;
            }

            .priority-score.priority-low .priority-badge {
                background: rgba(255, 255, 255, 0.2);
                color: white;
            }

            /* Responsive Design */
            @media (max-width: 1200px) {
                table {
                    min-width: 900px;
                }
                
                .task-title {
                    min-width: 200px;
                    max-width: 280px;
                }
                
                .metric-cell {
                    min-width: 100px;
                    width: 100px;
                }
                
                .priority-cell {
                    min-width: 120px;
                    width: 120px;
                }
            }

            @media (max-width: 768px) {
                .table-header {
                    padding: 20px 20px 15px;
                }
                
                .table-header h3 {
                    font-size: 18px;
                }
                
                table {
                    min-width: 700px;
                    font-size: 13px;
                }
                
                .th-content {
                    padding: 15px 12px;
                }
                
                td {
                    padding: 16px 12px;
                }
                
                .task-title {
                    min-width: 180px;
                    max-width: 220px;
                }
                
                .title-text strong {
                    font-size: 14px;
                }
                
                .metric-cell {
                    min-width: 90px;
                    width: 90px;
                }
                
                .metric-bar {
                    width: 60px;
                }
                
                .priority-cell {
                    min-width: 110px;
                    width: 110px;
                }
                
                .score-value {
                    font-size: 20px;
                }
            }

            @media (max-width: 480px) {
                .table-header {
                    padding: 15px 15px 12px;
                }
                
                .table-header h3 {
                    font-size: 16px;
                }
                
                .table-header p {
                    font-size: 12px;
                }
                
                table {
                    min-width: 600px;
                    font-size: 12px;
                }
                
                .th-content {
                    padding: 12px 10px;
                    font-size: 11px;
                }
                
                td {
                    padding: 12px 10px;
                }
                
                .rank-badge {
                    font-size: 10px;
                    padding: 3px 6px;
                    min-width: 24px;
                }
                
                .title-text strong {
                    font-size: 13px;
                }
                
                .task-description {
                    font-size: 11px;
                }
                
                .metric-icon {
                    font-size: 16px;
                }
                
                .metric-value {
                    font-size: 12px;
                }
                
                .metric-bar {
                    width: 50px;
                    height: 5px;
                }
                
                .hours-value {
                    font-size: 13px;
                }
                
                .hours-indicator {
                    font-size: 14px;
                }
                
                .score-value {
                    font-size: 18px;
                }
                
                .priority-badge {
                    font-size: 9px;
                    padding: 3px 6px;
                }
            }

            /* Loading Animation for New Data */
            @keyframes fadeInUp {
                from {
                    opacity: 0;
                    transform: translateY(20px);
                }
                to {
                    opacity: 1;
                    transform: translateY(0);
                }
            }

            .task-row {
                animation: fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
            }

            .task-row:nth-child(1) { animation-delay: 0.1s; }
            .task-row:nth-child(2) { animation-delay: 0.2s; }
            .task-row:nth-child(3) { animation-delay: 0.3s; }
            .task-row:nth-child(4) { animation-delay: 0.4s; }
            .task-row:nth-child(5) { animation-delay: 0.5s; }

            /* Print Styles */
            @media print {
                .task-table {
                    box-shadow: none;
                    border: 1px solid #ddd;
                }
                
                .table-header {
                    background: #f8f9fa !important;
                    color: #333 !important;
                }
                
                thead th {
                    background: #f8f9fa !important;
                    color: #333 !important;
                }
                
                .priority-score {
                    background: #f8f9fa !important;
                    color: #333 !important;
                    border: 1px solid #ddd;
                }
                
                .metric-bar,
                .metric-icon,
                .hours-indicator,
                .rank-badge {
                    display: none;
                }
            }
            `}</style>
        </div>
    );
};

export default TaskTable;